import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import useInView from "../hooks/useInView";
import { projects } from "../data/projects";

export default function Work() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [heroRef, heroInView] = useInView();
  const [ctaRef, ctaInView] = useInView();
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [visibleItems, setVisibleItems] = useState(new Set());
  const itemRefs = useRef([]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const filteredProjects = useMemo(() => {
    let filtered =
      activeFilter === "all"
        ? [...projects].sort((a, b) => a.allOrder - b.allOrder)
        : [...projects]
            .filter((p) => p.categories.includes(activeFilter))
            .sort((a, b) => a.allOrder - b.allOrder);

    const withHero = filtered.map((project) => {
      if (project.heroOnFilter && project.heroOnFilter.includes(activeFilter)) {
        return { ...project, size: "large", isHero: true };
      }
      return { ...project, isHero: project.size === "large" };
    });

    const heroIndex = withHero.findIndex((p) => p.isHero);
    if (heroIndex > 0) {
      const hero = withHero.splice(heroIndex, 1)[0];
      withHero.unshift(hero);
    }

    const hasWideAfterHero = withHero.slice(1, 3).some((p) => p.size === "wide");
    if (!hasWideAfterHero && withHero.length > 1) {
      if (withHero[1].size === "normal") {
        withHero[1] = { ...withHero[1], size: "wide" };
      }
    }

    let wideCount = 0;
    return withHero.map((project) => {
      if (project.size === "wide") {
        const offset = wideCount % 2 === 1;
        wideCount++;
        return { ...project, offset };
      }
      return project;
    });
  }, [activeFilter]);

  useEffect(() => {
    const observers = [];
    const refs = itemRefs.current;

    refs.forEach((ref, index) => {
      if (!ref) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        },
        { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
      );
      observer.observe(ref);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [filteredProjects.length, activeFilter]);

  const filters = [
    { id: "all", label: "ALL" },
    { id: "campaigns", label: "CAMPAIGNS" },
    { id: "systems", label: "SYSTEMS & IDENTITY" },
    { id: "institutions", label: "INSTITUTIONS" },
  ];

  const getGridClasses = (size, offset) => {
    if (isMobile) return "";
    if (isTablet) {
      switch (size) {
        case "large": return "col-span-2";
        case "wide": return "col-span-2";
        case "tall": return "row-span-2";
        default: return "";
      }
    }
    switch (size) {
      case "large": return "col-span-3";
      case "wide": return offset ? "col-span-2 col-start-2" : "col-span-2";
      case "tall": return "row-span-2";
      default: return "";
    }
  };

  const getAspectRatio = (size) => {
    if (isMobile) return "1/1";
    switch (size) {
      case "large": return "2/1";
      case "wide": return "16/9";
      case "tall": return "3/4";
      default: return "16/9";
    }
  };

  return (
    <>
      <style>{`
        .work-image {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-color: #27272a;
          filter: brightness(1.0);
          transition: filter 0.3s ease-out;
          will-change: filter;
          transform: translateZ(0);
        }
        .work-item:hover .work-image {
          filter: brightness(1.15);
        }
        .work-item {
          contain: layout;
        }
      `}</style>

      {/* Hero */}
      <section
        className="pt-75 md:pt-48 pb-28 md:pb-28"
        style={{
          paddingTop: isMobile ? "150px" : undefined,
          paddingBottom: isMobile ? "35px" : undefined,
          paddingLeft: isMobile ? "15px" : "48px",
          paddingRight: isMobile ? "15px" : "48px",
        }}
      >
        <div className="container-max text-center">
          <h2 ref={heroRef} className="heading-section">
            <span
              className="block"
              style={{
                opacity: heroInView ? 1 : 0,
                filter: `blur(${heroInView ? 0 : 10}px)`,
                transform: `translateY(${heroInView ? 0 : -20}px)`,
                transition: "opacity 0.4s ease-out, filter 0.4s ease-out, transform 0.4s ease-out",
              }}
            >
              PROJECTS THAT
            </span>
            <span
              className="block"
              style={{
                opacity: heroInView ? 1 : 0,
                filter: `blur(${heroInView ? 0 : 10}px)`,
                transform: `translateY(${heroInView ? 0 : -20}px)`,
                transition: "opacity 0.4s ease-out 0.0375s, filter 0.4s ease-out 0.0375s, transform 0.4s ease-out 0.0375s",
              }}
            >
              SURVIVED BRIEFINGS,
            </span>
            <span
              className="block"
              style={{
                opacity: heroInView ? 1 : 0,
                filter: `blur(${heroInView ? 0 : 10}px)`,
                transform: `translateY(${heroInView ? 0 : -20}px)`,
                transition: "opacity 0.4s ease-out 0.075s, filter 0.4s ease-out 0.075s, transform 0.4s ease-out 0.075s",
              }}
            >
              CLIENTS, AND ME
            </span>
          </h2>
        </div>
      </section>

      {/* Filter */}
      <section
        style={{
          paddingLeft: isMobile ? "15px" : "48px",
          paddingRight: isMobile ? "15px" : "48px",
        }}
      >
        <div className="max-w-full">
          <div
            className="flex gap-6 justify-center"
            style={{
              flexWrap: "wrap",
              rowGap: isMobile ? "4px" : undefined,
            }}
          >
            {filters.map((filter, index) => (
              <React.Fragment key={filter.id}>
                <button
                  onClick={() => {
                    setActiveFilter(filter.id);
                    setVisibleItems(new Set());
                  }}
                  className="py-2 bg-transparent border-none cursor-pointer text-sm font-semibold uppercase tracking-wider font-inter whitespace-nowrap transition-colors flex items-center gap-1"
                  style={{ color: activeFilter === filter.id ? "#fff" : "#71717a", lineHeight: "1" }}
                  onMouseEnter={(e) => { if (activeFilter !== filter.id) e.currentTarget.style.color = "#d4d4d8"; }}
                  onMouseLeave={(e) => { if (activeFilter !== filter.id) e.currentTarget.style.color = "#71717a"; }}
                >
                  <span className="text-lg font-light">[</span>
                  <span>{filter.label}</span>
                  <span className="text-lg font-light">]</span>
                </button>
                {isMobile && index === 1 && <div style={{ width: "100%", height: 0 }} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section
        className="py-16 bg-black"
        style={{
          paddingLeft: isMobile ? "15px" : "48px",
          paddingRight: isMobile ? "15px" : "48px",
          paddingBottom: isMobile ? "35px" : "128px",
          paddingTop: isMobile ? "20px" : "48px",
        }}
      >
        <div className="max-w-full">
          <div
            className="grid gap-x-8 gap-y-5"
            style={{
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                ? "repeat(2, 1fr)"
                : "repeat(3, 1fr)",
              gridAutoFlow: "dense",
            }}
          >
            {filteredProjects.map((project, index) => {
              const isVisible = visibleItems.has(index);

              return (
                <Link
                  to={`/work/${project.slug}`}
                  key={project.id}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`work-item cursor-pointer flex flex-col ${getGridClasses(project.size, project.offset)}`}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    filter: `blur(${isVisible ? 0 : 8}px)`,
                    transform: `translateY(${isVisible ? 0 : -15}px)`,
                    transition: "opacity 0.6s ease-out, filter 0.6s ease-out, transform 0.6s ease-out",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div
                    className="w-full min-h-0 flex-grow rounded-sm overflow-hidden mb-1.5"
                    style={{
                      position: "relative",
                      aspectRatio: getAspectRatio(project.size),
                    }}
                  >
                    <div
                      className="work-image"
                      style={{ backgroundImage: `url(${project.image})` }}
                    />
                  </div>

                  <p className="body-small leading-snug">
                    <strong className="font-extrabold font-archivo text-white">
                      {project.title}
                    </strong>
                    {" | "}
                    <span className="text-zinc-500">{project.desc}</span>
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={ctaRef}
        style={{
          padding: isMobile ? "64px 15px" : "128px 48px",
          backgroundColor: "#09090b",
        }}
      >
        <div style={{ maxWidth: "1024px", margin: "0 auto", textAlign: "center" }}>
          <h2
            style={{
              fontFamily: "Archivo, sans-serif",
              fontWeight: "900",
              letterSpacing: "-0.02em",
              fontVariationSettings: '"wght" 650, "wdth" 75',
              fontSize: "clamp(40px, 6vw + 16px, 90px)",
              lineHeight: "0.95",
              marginBottom: "24px",
              textTransform: "uppercase",
            }}
          >
            <span
              style={{
                display: "block",
                opacity: ctaInView ? 1 : 0,
                filter: `blur(${ctaInView ? 0 : 10}px)`,
                transform: `translateY(${ctaInView ? 0 : -20}px)`,
                transition: "opacity 0.8s ease-out 0.5s",
              }}
            >
              READY <br /> WHEN YOU ARE
            </span>
          </h2>
          <p
            style={{
              fontSize: "20px",
              color: "#9ca3af",
              lineHeight: 1.6,
              marginBottom: "56px",
              maxWidth: "800px",
              margin: "0 auto 56px",
            }}
          >
            Liked what you saw? Let’s talk. <br />
            Didn’t like it? Let’s talk anyway – I like a challenge.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
              opacity: ctaInView ? 1 : 0,
              transform: `translateY(${ctaInView ? 0 : -20}px)`,
              filter: `blur(${ctaInView ? 0 : 10}px)`,
              transition: "opacity 0.8s ease-out 0.8s, transform 0.8s ease-out 0.8s, filter 0.8s ease-out 0.8s",
            }}
          >
            <Button href="/contact" internal>E-Mail me</Button>
            <Button href="/contact" internal>Whatsapp me</Button>
          </div>
        </div>
      </section>
    </>
  );
}